import React, { useState, useEffect } from 'react';

interface NetworkLog {
  type: 'request' | 'success' | 'fail';
  url: string;
  method?: string;
  status?: number;
  message?: string;
  timestamp: string;
}

const DebugDrawer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<NetworkLog[]>([]);

  useEffect(() => {
    // Initial load
    if (typeof window !== 'undefined') {
      setLogs((window as any).__NETWORK_LOGS__ || []);
    }

    const handleUpdate = () => {
      setLogs([...((window as any).__NETWORK_LOGS__ || [])]);
    };

    window.addEventListener('network_log_updated', handleUpdate);
    return () => window.removeEventListener('network_log_updated', handleUpdate);
  }, []);

  const clearLogs = () => {
    if (typeof window !== 'undefined') {
      (window as any).__NETWORK_LOGS__ = [];
      window.dispatchEvent(new CustomEvent('network_log_updated'));
    }
  };

  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '100px',
          right: '20px',
          zIndex: 999999,
          background: '#ff5722',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '12px',
        }}
      >
        {isOpen ? '✕' : 'BUG'}
      </button>

      {/* Drawer Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100vw',
            height: '60vh',
            background: 'rgba(25, 25, 25, 0.96)',
            color: '#e0e0e0',
            zIndex: 999998,
            borderTop: '4px solid #ff5722',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'monospace',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#1a1a1a',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <span style={{ fontWeight: 'bold', color: '#ff5722' }}>Network Logs ({logs.length})</span>
            <div>
              <button
                onClick={clearLogs}
                style={{
                  background: 'transparent',
                  color: '#aaa',
                  border: '1px solid #555',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginRight: '8px',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'transparent',
                  color: '#e0e0e0',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Logs List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                No network activity logged yet.
              </div>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px',
                    borderBottom: '1px solid #333',
                    fontSize: '12px',
                    background: log.type === 'fail' ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span
                      style={{
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontWeight: 'bold',
                        fontSize: '10px',
                        background:
                          log.type === 'request' ? '#2196F3' :
                          log.type === 'success' ? '#4CAF50' : '#F44336',
                        color: 'white',
                      }}
                    >
                      {log.type.toUpperCase()}
                    </span>
                    <span style={{ color: '#888', fontSize: '10px' }}>{log.timestamp}</span>
                  </div>
                  <div>
                    <strong>{log.method}</strong> {log.url}
                  </div>
                  {log.status && (
                    <div style={{ color: log.status < 400 ? '#8bc34a' : '#f44336', marginTop: '2px' }}>
                      Status: {log.status}
                    </div>
                  )}
                  {log.message && (
                    <div style={{ color: '#aaa', marginTop: '2px', wordBreak: 'break-all', fontSize: '11px' }}>
                      {log.message}
                    </div>
                  )}
                  {/* Expandable data payload view */}
                  {(log as any).data && (
                    <details style={{ marginTop: '4px' }}>
                      <summary style={{ cursor: 'pointer', color: '#ff5722', fontSize: '11px', outline: 'none' }}>
                        View Payload
                      </summary>
                      <pre style={{ 
                        overflowX: 'auto', 
                        background: '#222', 
                        padding: '6px', 
                        borderRadius: '4px',
                        fontSize: '10px', 
                        marginTop: '4px',
                        maxHeight: '180px',
                        border: '1px solid #333',
                        color: '#64B5F6',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                      }}>
                        {JSON.stringify((log as any).data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DebugDrawer;
